import { useMemo, FunctionComponent, useId, useCallback, ChangeEvent, ComponentType } from 'react';
import classnames from 'classnames';
import CreatableSelect from 'react-select/creatable';
import { MapMarkerAltLightIcon } from '../../../../components/Icons';
import { WebCourseSearchFilters } from '../../../../services/search/course/useCourseSearch';
import {
    DEFAULT_RADIUS,
    locationsWithGeoCoordinates,
    LocationWithGeoCoordinates,
} from '../../../../services/search/locationFilter';
import FacetFilterSection from '../FacetFilterSection';
import styles from './styles.module.scss';
import { FormatOptionLabelMeta } from 'react-select';

type LocationFacetFilterProps = {
    value: WebCourseSearchFilters['location'];
    onChange: (newValue: WebCourseSearchFilters['location']) => void;
    hasInvalidLocation: boolean;
};

type LocationOption =
    | {
          label: string;
          value: string;
          location: LocationWithGeoCoordinates;
      }
    | {
          label: string;
          value: string;
          __isNew__: boolean;
      };
const locationOptions: Array<LocationOption> = locationsWithGeoCoordinates.map((location) => {
    return {
        label: `${location.name}, ${location.ceremonialArea}`,
        value: location.name,
        location,
    };
});

const formatOptionLabel = (option: LocationOption, meta: FormatOptionLabelMeta<LocationOption>) => {
    const isThisOptionSelected = meta.selectValue.some(
        (selected) => selected.value === option.value
    );
    if (isThisOptionSelected === true) {
        return option.value;
    }
    if ('location' in option) {
        const location = option.location;
        const tag =
            location.name === location.ceremonialArea
                ? `${location.flag} ${location.name}`
                : `${location.flag} ${location.ceremonialArea}`;
        return (
            <div className={styles.selectOption}>
                <span>{location.name}</span>
                <span className={styles.selectOptionTag}>{tag}</span>
            </div>
        );
    }
    return <span>{option.label}</span>;
};

const formatCreateLabel = (searchTerm: string) => `Search for "${searchTerm}"`;

type LocationRadiusInputProps = {
    radiusInputValue: number;
    locationSelectValue: LocationOption | null;
    handleRadiusInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const LocationRadiusInput: FunctionComponent<LocationRadiusInputProps> = ({
    radiusInputValue,
    locationSelectValue,
    handleRadiusInputChange,
}) => {
    const id = useId();

    if (!locationSelectValue) {
        return null;
    }

    return (
        <>
            <div className={styles.radiusLabel}>
                <label htmlFor={`${id}-radiusInput`}>
                    <span>Radius</span>
                </label>
                <span>{radiusInputValue} Miles</span>
            </div>
            <input
                id={`${id}-radiusInput`}
                type="range"
                min={1}
                max={100}
                value={radiusInputValue}
                onChange={handleRadiusInputChange}
            />
        </>
    );
};

export const LocationFacetFilter: FunctionComponent<LocationFacetFilterProps> = ({
    value,
    onChange,
    hasInvalidLocation,
}) => {
    const locationSelectValue = useMemo((): LocationOption | null => {
        if (!value) {
            return null;
        }
        if ('known_location' in value) {
            const locationOption = locationOptions.find((option) => {
                return option.value === value.known_location;
            });
            if (locationOption) {
                return locationOption;
            } else {
                return {
                    label: value.known_location,
                    value: value.known_location,
                    __isNew__: true,
                };
            }
        }
        return {
            label: value.query,
            value: value.query,
            __isNew__: true,
        };
    }, [value]);

    const handleSelectInputChange = useCallback(
        (newLocation: LocationOption | null) => {
            if (!newLocation) {
                return onChange(undefined);
            }
            if ('location' in newLocation) {
                return onChange({
                    known_location: newLocation.location.name,
                    radius: value?.radius || DEFAULT_RADIUS,
                });
            }
            return onChange({
                query: newLocation.value,
                radius: value?.radius || DEFAULT_RADIUS,
            });
        },
        [value, onChange]
    );

    const radiusInputValue = useMemo(() => {
        if (!value) {
            return DEFAULT_RADIUS.value;
        }
        return value.radius.value;
    }, [value]);
    const handleRadiusInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!value) {
                return onChange(undefined);
            }
            if (!event.target.value) {
                return onChange({
                    ...value,
                    radius: DEFAULT_RADIUS,
                });
            }
            return onChange({
                ...value,
                radius: {
                    value: Number(event.target.value),
                    unit: 'miles',
                },
            });
        },
        [value, onChange]
    );

    return (
        <FacetFilterSection
            name={'Location'}
            ariaLabel={'Location'}
            numberOfIntiallyVisible={Infinity}
        >
            <li className={styles.container}>
                <div className={styles.locationInputContainer}>
                    <MapMarkerAltLightIcon
                        className={classnames(styles.locationInputIcon, {
                            [styles.invalid]: hasInvalidLocation,
                        })}
                    />
                    <CreatableSelect
                        aria-label="Location"
                        options={locationOptions}
                        formatOptionLabel={formatOptionLabel}
                        formatCreateLabel={formatCreateLabel}
                        hideSelectedOptions={true}
                        isClearable={true}
                        value={locationSelectValue}
                        onChange={handleSelectInputChange}
                        classNames={{
                            control: () =>
                                classnames(styles.selectControl, {
                                    [styles.invalid]: hasInvalidLocation,
                                }),
                            clearIndicator: () =>
                                classnames(styles.clearIndicator, {
                                    [styles.invalid]: hasInvalidLocation,
                                }),
                            dropdownIndicator: () =>
                                classnames(styles.dropdownIndicator, {
                                    [styles.invalid]: hasInvalidLocation,
                                }),
                        }}
                    />
                </div>
                <LocationRadiusInput
                    radiusInputValue={radiusInputValue}
                    locationSelectValue={locationSelectValue}
                    handleRadiusInputChange={handleRadiusInputChange}
                />
            </li>
            <></>
        </FacetFilterSection>
    );
};

export default LocationFacetFilter;
